'use client'

import React from 'react'
import { useCart } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import { BarcodeBadge, PriceBadge, SkuBadge, Thumbnail } from '@/components/ui'

interface ProductCardHorizontalProps
  extends React.ComponentPropsWithoutRef<'div'> {
  product: Product & { qty?: number }
  priceBadgeClassName?: string
  headingClassName?: string
  priceClassName?: string
}

export const ProductCardHorizontal = ({
  product = {} as Product,
  className,
  priceBadgeClassName,
  headingClassName,
  priceClassName,
  ...restProps
}: ProductCardHorizontalProps) => {
  const { selectProduct }: any = useCart()
  const fallbackTitle = `${product?.product_name ?? product?.title} ${product?.varient_options}`

  return (
    <div
      className={cn(
        'group flex cursor-pointer rounded-lg border-gray-100 bg-white shadow-card hover:dark:border-gray-700',
        className,
      )}
      onClick={() => selectProduct(product)}
      {...restProps}
    >
      <Thumbnail
        className="aspect-square h-auto w-[100px] rounded-r-none border-0 !bg-gray-100"
        size="md"
        imageClassName="object-center"
        src={product.image_url}
      />
      <div className="flex flex-col gap-2 px-4 pb-5 pt-4">
        <div className="flex w-full flex-col gap-1">
          <h2
            className={cn(
              'line-clamp-2 text-sm font-medium leading-snug text-gray-950 group-hover:text-blue-600',
              headingClassName,
            )}
          >
            <span>{product.variant_label ?? fallbackTitle}</span>
          </h2>
        </div>

        <div className="flex flex-col gap-1">
          <PriceBadge
            price={product?.default_price?.amount}
            badge={{
              className: priceBadgeClassName,
            }}
            className={cn('text-gray-950', priceClassName)}
          />

          <div className="flex flex-col gap-2 md:flex-row">
            <SkuBadge
              badge={{
                className: 'text-gray-700 border-gray-600',
              }}
              className="items-start text-xs font-medium text-gray-600"
            >
              {product.sku}
            </SkuBadge>

            <BarcodeBadge
              className="items-center text-xs font-medium text-gray-600"
              badge={{ className: 'w-7' }}
            >
              {product?.default_price?.barcode}
            </BarcodeBadge>
          </div>
        </div>
      </div>
    </div>
  )
}
